import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Brush,
  ResponsiveContainer,
} from "recharts";
import {
  Card,
  CardContent,
  Paper,
  Tabs,
  Tab,
  Box,
  makeStyles,
  withStyles
} from "@material-ui/core";
import Space from "../../components/Space";
import { weather_icon } from "../../constants/values";

const SectionTitle = styled.h2`
  font-weight: bold;
  margin-left: 16px;
  margin-bottom: 8px;
  font-size: 18px;
  color: #333;
`;

const MyRadonLineChart = (param) => {
  const { data, isUs = true } = param;
  const radonUnitStr = isUs ? "pCi/L" : "Bq/m³";
  const [reportData, setReportData] = useState(data || []);
  const [selectedTab, setSelectedTab] = useState(0);
  const [fixedBrushStart, setFixedBrushStart] = useState(0);
  const [fixedBrushEnd, setFixedBrushEnd] = useState(12);
  const [visibleRange, setVisibleRange] = useState([fixedBrushStart, fixedBrushEnd]);
  const [interval, setInterval] = useState(0);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const classes = useStyles({ screenWidth });
  const [brushInitialized, setBrushInitialized] = useState(false);

  useEffect(() => {
    if (data && data.length > 0) {
      setReportData(data);
      const defaultEnd = Math.min(12, data.length - 1);
      setFixedBrushEnd(defaultEnd);
      setVisibleRange([0, defaultEnd]);
    } else {
      setReportData([]);
    }
  }, [data]);

  const [yAxisLeftDomain, setYAxisLeftDomain] = useState(['auto', 'auto']);
  const [yAxisRightDomain, setYAxisRightDomain] = useState(['auto', 'auto']);
  const [yAxisLeftTicks, setYAxisLeftTicks] = useState([]); // 왼쪽 Y축 눈금
  const [yAxisRightTicks, setYAxisRightTicks] = useState([]); // 오른쪽 Y축 눈금

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      if (payload.length === 0) return null;

      const labelMap = {
        value1: `Radon (${radonUnitStr})`,
        value2: isUs ? "Temperature (°F)" : "Temperature (°C)",
        value3: "Humidity (%)",
        value4: "Wind (mph)",
        value5: isUs ? "Pressure (inHg)" : "Pressure (hPa)",
      };

      const value0 = payload.find(item => item.name === 'value0');
      const value1 = payload.find(item => item.name === 'value1');

      const filteredPayload = value1 ? payload.filter(item => item.name !== 'value0') : payload;

      return (
        <div
          style={{
            backgroundColor: "#f9f9f9",
            color: "#333",
            border: `1px solid #A9A9A9`,
            padding: 10,
            paddingTop: 0,
          }}
        >
          <p>{label}</p>
          {filteredPayload.map((item, index) => {
            let displayedValue = item.value;
            // 툴팁에서도 소수점 자릿수 조정
            if (item.name === 'value1') {
              displayedValue = typeof item.value === 'number' ? item.value.toFixed(2) : item.value;
            } else if (item.name === 'value4') {
              displayedValue = typeof item.value === 'number' ? item.value.toFixed(1) : item.value;
            } else if (item.name === 'value5') {
              displayedValue = typeof item.value === 'number' ? item.value.toFixed(2) : item.value;
            } else {
              displayedValue = typeof item.value === 'number' ? Math.round(item.value) : item.value;
            }

            return item.name === 'value0' && !value1 ? (
              <p key={index} style={{ color: "#3DB5E6", margin: 0 }}>
                Radon ({radonUnitStr}) : N/A
              </p>
            ) : (
              <p key={index} style={{ color: item.color, margin: 0 }}>
                {labelMap[item.name] || item.name} : {displayedValue}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const getLineColors = () => {
    switch (selectedTab) {
      case 0:
        return {
          radonColor: "#3DB5E6",
          secondLineColor: "#FF9D66",
          tooltipTextColor: "#A9A9A9",
          labelColor: "#FFBC8C",
        };
      case 1:
        return {
          radonColor: "#3DB5E6",
          secondLineColor: "#897FC8",
          tooltipTextColor: "#A9A9A9",
          labelColor: "#897FC8",
        };
      case 2:
        return {
          radonColor: "#3DB5E6",
          secondLineColor: "#6BC04B",
          tooltipTextColor: "#A9A9A9",
          labelColor: "#6BC04B",
        };
      case 3:
        return {
          radonColor: "#3DB5E6",
          secondLineColor: "#8E59EF",
          tooltipTextColor: "#A9A9A9",
          labelColor: "#8E59EF",
        };
      default:
        return {
          radonColor: "#3DB5E6",
          secondLineColor: "#FF9D66",
          tooltipTextColor: "#A9A9A9",
          labelColor: "#FFBC8C",
        };
    }
  };

  const [tooltipTextColor, setTooltipTextColor] = useState("#A9A9A9");

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      if (width < 600) {
        setInterval(1);
      } else if (width < 960) {
        setInterval(0);
      } else {
        setInterval(0);
      }
    };
    setTooltipTextColor(getLineColors().tooltipTextColor);
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [selectedTab]);

  useEffect(() => {
    if (!reportData || reportData.length === 0) {
      setYAxisLeftDomain([0, 'auto']);
      setYAxisRightDomain([0, 'auto']);
      setYAxisLeftTicks([0, 25, 50, 75, 100]); // 기본 라돈 눈금
      setYAxisRightTicks([0, 25, 50, 75, 100]); // 기본 날씨 눈금
      return;
    }

    const currentViewData = reportData.slice(visibleRange[0], visibleRange[1] + 1);

    let leftMin = Infinity;
    let leftMax = -Infinity;
    currentViewData.forEach(item => {
      const v0 = Number(item.value0);
      if (item.value0 !== undefined && item.value0 !== null && !isNaN(v0)) {
        leftMin = Math.min(leftMin, v0);
        leftMax = Math.max(leftMax, v0);
      }
      const v1 = Number(item.value1);
      if (item.value1 !== undefined && item.value1 !== null && !isNaN(v1)) {
        leftMin = Math.min(leftMin, v1);
        leftMax = Math.max(leftMax, v1);
      }
    });

    let rightMin = Infinity;
    let rightMax = -Infinity;
    const rightDataKey =
      selectedTab === 0
        ? "value2"
        : selectedTab === 1
          ? "value3"
          : selectedTab === 2
            ? "value4"
            : "value5";

    reportData.forEach(item => {
      const val = Number(item[rightDataKey]);
      if (item[rightDataKey] !== undefined && item[rightDataKey] !== null && !isNaN(val)) {
        rightMin = Math.min(rightMin, val);
        rightMax = Math.max(rightMax, val);
      }
    });

    const bufferPercentage = 0.20;

    const calculateBufferedDomainAndTicks = (minVal, maxVal, isWind = false) => {
      if (
        minVal === Infinity ||
        maxVal === -Infinity ||
        isNaN(minVal) ||
        isNaN(maxVal)
      ) {
        // 데이터가 없으면 [0, auto] 도메인과 기본 눈금 반환
        return { domain: [0, 'auto'], ticks: [0, 25, 50, 75, 100] };
      }

      const lowerBound = 0;
      let upperBound = maxVal * (1 + bufferPercentage); // 최대값 + 20%

      // 상한이 하한보다 작거나 같아지는 경우 및 NaN 방지
      if (upperBound <= lowerBound || isNaN(upperBound)) {
        upperBound = lowerBound + (maxVal > 0 ? maxVal * bufferPercentage : 1);
        if (upperBound <= lowerBound || isNaN(upperBound)) upperBound = lowerBound + 1; // 최소 1의 간격 보장
      }

      let ticks;
      if (isWind) {
        // Wind: 고정된 상대 위치(0%, 25%, 50%, 75%, 100%)에 숫자 스케일링
        const fixedPositions = [0, 0.25, 0.5, 0.75, 1];
        ticks = fixedPositions.map(pos => Number((pos * upperBound).toFixed(1)));
      } else {
        // Pressure: 0부터 maxVal * 1.2까지 5개 눈금, 최상단은 maxVal * 1.2
        const fixedPositions = [0, 0.25, 0.5, 0.75, 1];
        ticks = fixedPositions.map(pos => Number((pos * upperBound).toFixed(2)));
      }

      ticks = ticks.filter(t => !isNaN(t));
      if (ticks.length === 0) ticks = [0, 25, 50, 75, 100];

      return { domain: [lowerBound, upperBound], ticks: ticks };
    };

    const { domain: leftDomain, ticks: leftTicks } = calculateBufferedDomainAndTicks(leftMin, leftMax);
    setYAxisLeftDomain(leftDomain);
    setYAxisLeftTicks(leftTicks);

    if (selectedTab === 0) {
      // Temperature: 고정된 도메인과 눈금 (US: 0~120°F, non-US: 0~50°C)
      if (isUs) {
        setYAxisRightDomain([0, 120]);
        setYAxisRightTicks([0, 30, 60, 90, 120]);
      } else {
        setYAxisRightDomain([0, 50]);
        setYAxisRightTicks([0, 10, 20, 30, 40, 50]);
      }
    } else if (selectedTab === 1) {
      // Humidity: 고정된 도메인과 눈금
      setYAxisRightDomain([0, 100]);
      setYAxisRightTicks([0, 25, 50, 75, 100]);
    } else {
      // Wind or Pressure: 동적 도메인과 눈금 (20% 버퍼 포함)
      const { domain: rightDomain, ticks: rightTicks } = calculateBufferedDomainAndTicks(rightMin, rightMax, selectedTab === 2);
      setYAxisRightDomain(rightDomain);
      setYAxisRightTicks(rightTicks);
    }

  }, [reportData, selectedTab, visibleRange]);

  const CustomTabs = withStyles({
    root: {
      minHeight: "36px",
    },
    indicator: {
      display: "none",
    },
  })(Tabs);

  const CustomTab = withStyles((theme) => ({
    root: {
      padding: "4px 10px",
      margin: "0 6px",
      borderRadius: "20px",
      border: "1px solid #565191",
      color: "#565191",
      fontSize: "12px",
      textTransform: "none",
      minHeight: "30px",
      "&.Mui-selected": {
        color: "#FFFFFF",
        backgroundColor: getLineColors().secondLineColor,
        border: "none",
      },
      [theme.breakpoints.down(750)]: {
        padding: "2px 6px",
        margin: "0 5px",
        fontSize: "12px",
        minWidth: "100px",
      },
      [theme.breakpoints.down(500)]: {
        padding: "1px 15px",
        margin: "0 5px",
        fontSize: "12px",
        minWidth: "50px",
      },
    },
  }))((props) => {
    const { selectedTab, ...otherProps } = props;
    return <Tab {...otherProps} />;
  });

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleBrushChange = (e) => {
    setVisibleRange([e.startIndex, e.endIndex]);
  };

  const renderWeatherIcon = (weather) => {
    if (weather) {
      let capitalizedWeather = weather.charAt(0).toUpperCase() + weather.slice(1).toLowerCase();
      const weatherData = weather_icon.find(w => w.day === capitalizedWeather || w.night === capitalizedWeather);

      return weatherData ? `https://cdn.weatherapi.com/weather/64x64/day/${weatherData.icon}.png` : null;
    }

    return null;
  };

  const renderChart = () => {
    const { radonColor, secondLineColor } = getLineColors();

    return (
      <div className={classes.chartContainer}>
        <ResponsiveContainer width="100%" className={classes.centerContainer}>
          <LineChart data={reportData} margin={{ top: 20, right: 0, left: 0, bottom: 37 }}>
            <CartesianGrid stroke="#D3D3D3" strokeDasharray="1 3" />
            <XAxis
              interval={interval}
              dataKey="name"
              label={{ value: "", position: "insideBottom", offset: -10 }}
              tick={({ x, y, payload }) => {
                const dataIndex = visibleRange[0] + payload.index;
                if (dataIndex >= reportData.length || dataIndex < 0) {
                  return null;
                }
                const item = reportData[dataIndex];
                const weather = item?.weather;
                const iconUrl = renderWeatherIcon(weather);

                const input = item.name;

                const dateObj = new Date(input);

                const hours = String(dateObj.getHours()).padStart(2, '0');
                const minutes = String(dateObj.getMinutes()).padStart(2, '0');
                const militaryTime = `${hours}:${minutes}`;

                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const day = String(dateObj.getDate()).padStart(2, '0');
                const formattedDate = `${month}/${day}`;

                return (
                  <g transform={`translate(${x},${y})`}>
                    <text x={0} y={0} dy={12} textAnchor="middle" fill="#666" fontSize={10}>
                      {militaryTime}
                    </text>
                    <text x={0} y={0} dy={21} textAnchor="middle" fill="#666" fontSize={9}>
                      {formattedDate}
                    </text>
                    {iconUrl && (
                      <foreignObject x="-12" y="-284" width="24" height="24">
                        <img src={iconUrl} width="24" height="24" alt="weather icon" />
                      </foreignObject>
                    )}
                  </g>
                );
              }}
              tickLine={{ stroke: "#D3D3D3" }}
            />

            <YAxis
              yAxisId="left"
              tickFormatter={(value) => value === 0 ? "0" : (typeof value === 'number' ? value.toFixed(2) : value)} // 라돈 0은 "0", 나머지는 소수점 2자리
              ticks={yAxisLeftTicks} // 계산된 왼쪽 Y축 눈금 적용
              tick={{ fontSize: 10, fill: "#666" }}
              label={null}
              domain={yAxisLeftDomain}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={(value) =>
                selectedTab === 2 ? Number(value.toFixed(1)) :
                  selectedTab === 3 ? Number(value.toFixed(2)) :
                    Math.round(value)
              } // Wind는 소수점 1자리, Pressure는 소수점 2자리, 나머지는 정수
              ticks={yAxisRightTicks} // 계산된 또는 고정된 오른쪽 Y축 눈금 적용
              tick={{ fontSize: 10, fill: "#666" }}
              label={null}
              domain={yAxisRightDomain}
            />
            <Tooltip content={<CustomTooltip />} />

            <Line
              yAxisId="left"
              type="linear"
              dataKey="value0"
              stroke={'#808080'}
              dot={false}
              strokeDasharray="4 4"
            />
            <Line
              yAxisId="left"
              type="linear"
              dataKey="value1"
              stroke={radonColor}
              dot={true}
              strokeWidth={2}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey={
                selectedTab === 0
                  ? "value2"
                  : selectedTab === 1
                    ? "value3"
                    : selectedTab === 2
                      ? "value4"
                      : "value5"
              }
              stroke={secondLineColor}
              dot={false}
              strokeDasharray="8 8"
              connectNulls={true}
            />
            <Brush
              dataKey="name"
              startIndex={!brushInitialized ? fixedBrushStart : undefined}
              endIndex={!brushInitialized ? fixedBrushEnd : undefined}
              onChange={(e) => {
                if (!brushInitialized) setBrushInitialized(true);
                handleBrushChange(e);
              }}
              height={70}
              y={320}
              fill="#FFF"
              stroke="#ABABAB"
              travellerWidth={1}
              padding={{ top: 0, right: 0, bottom: 0, left: 0 }}
            >
              <LineChart>
                <CartesianGrid stroke="#D3D3D3" strokeDasharray="1 3" />
                <Line
                  type="linear"
                  dataKey="value0"
                  stroke={'#808080'}
                  strokeDasharray="4 4"
                  dot={false}
                />
                <Line
                  type="linear"
                  dataKey="value1"
                  stroke={getLineColors().radonColor}
                  strokeWidth={1.5}
                  dot={false}
                />
              </LineChart>
            </Brush>

          </LineChart>

        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <Card className={classes.root} elevation={0}>
      <CardContent className={classes.cardContent}>
        <CustomTabs value={selectedTab} onChange={handleTabChange} centered style={{ marginTop: 16 }}>
          <CustomTab label={screenWidth <= 600 ? "Temp" : "Temperature"} selectedTab={selectedTab === 0} />
          <CustomTab label={screenWidth <= 600 ? "Humid" : "Humidity"} selectedTab={selectedTab === 1} />
          <CustomTab label={screenWidth <= 600 ? "Wind" : "Wind"} selectedTab={selectedTab === 2} />
          <CustomTab label={screenWidth <= 600 ? "Press" : "Pressure"} selectedTab={selectedTab === 3} />
        </CustomTabs>
        <Box className={classes.tabContent}>
          <div className={classes.brushContainer}>
            <Space height={"20px"} />
            <div className={classes.axisLabel}>
              <span style={{ color: getLineColors().radonColor, marginLeft: "20px", fontSize: 9 }}>
                Radon ({radonUnitStr})
              </span>
              <span style={{ color: getLineColors().labelColor, marginRight: "20px", fontSize: 9 }}>
                {selectedTab === 0
                  ? (isUs ? "Temperature (°F)" : "Temperature (°C)")
                  : selectedTab === 1
                    ? "Humidity (%)"
                    : selectedTab === 2
                      ? "Wind (mph)"
                      : (isUs ? "Pressure (inHg)" : "Pressure (hPa)")}
              </span>
            </div>
            {renderChart()}
            <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", marginTop: "0px", paddingTop: "0px", paddingLeft: "20px", paddingRight: "20px" }}>
              {/* Radon 라벨 */}
              <div style={{ display: "flex", alignItems: "flex-start", marginRight: "20px", backgroundColor: "white", }}>
                <span
                  style={{
                    display: "inline-block",
                    width: "30px",
                    height: "4px",
                    backgroundColor: "#3DB5E6",
                    marginRight: "10px",
                    marginTop: "4px"
                  }}
                ></span>
                <span style={{ fontSize: "11px", color: "#333" }}>Radon</span>
              </div>

              {/* Weather 라벨 */}
              <div style={{ display: "flex", alignItems: "flex-start", backgroundColor: "white" }}>
                <span
                  style={{
                    display: "inline-block",
                    width: "30px",
                    height: "4px",
                    borderTop: "4px dashed",
                    borderColor: getLineColors().labelColor,
                    marginRight: "10px",
                    marginTop: "4px"
                  }}
                ></span>
                <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2" }}>
                  <span style={{ fontSize: "11px", color: "#333" }}>
                    {selectedTab === 0
                      ? "Outside Temperature"
                      : selectedTab === 1
                        ? "Outside Humidity"
                        : selectedTab === 2
                          ? "Outside Wind"
                          : "Outside Pressure"}
                  </span>
                  <span style={{ fontSize: "9px", color: "#333" }}>(Weather-based reference)</span>
                </div>
              </div>
            </div>
          </div>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MyRadonLineChart;

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    height: "100%",
    backgroundColor: "white",
    margin: 0,
    padding: 0,
  },
  tabContent: {
    backgroundColor: "white",
    minHeight: "450px",
  },
  centerContainer: {
    justifyContent: "center",
    alignItems: "center",
  },
  brushContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  axisLabel: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 14,
  },
  cardContent: {
    padding: 0,
    margin: 0,
  },
  chartContainer: {
    width: "100%",
    height: "410px",
    position: "relative",
    marginTop: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  "@media (max-width: 400px)": {
    root: {
      fontSize: "10px",
    },
    axisLabel: {
      fontSize: "10px",
    },
    chartContainer: {
      width: "115%",
      marginLeft: "-7%",
      paddingLeft: 0,
      paddingRight: 0,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    cardContent: {
      marginTop: "8px",
    },
  },
  "@media (min-width: 400px) and (max-width: 600px)": {
    chartContainer: {
      width: "110%",
      marginLeft: "-5%",
      paddingLeft: 0,
      paddingRight: 0,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }
  },
  "@global": {
    ".recharts-brush-slide": {
      fill: "#3DB5E6 !important",
    },
    ".recharts-brush-traveller": {
      display: "none",
    },
  },
}));
